import React, { useEffect, useState, useCallback, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import api, { apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { EmptyState } from "@/components/common/EmptyState";
import { UserAvatar } from "@/components/common/UserAvatar";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { MessageSquare, Send, ArrowLeft, UserPlus, CheckCircle2, ExternalLink, Search, Zap, Users2, BarChart3, Circle } from "lucide-react";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";

const TABS = [["open", "Open"], ["waiting", "Waiting"], ["mine", "Mine"], ["unassigned", "Unassigned"], ["closed", "Closed"]];
const STATUS_LABEL = { open: "Open", waiting_for_staff: "Waiting for Staff", waiting_for_candidate: "Waiting for Candidate", closed: "Closed" };

export default function SupportInbox() {
  const [params] = useSearchParams();
  const [tab, setTab] = useState("open");
  const [convs, setConvs] = useState([]);
  const [active, setActive] = useState(null);
  const [msgs, setMsgs] = useState([]);
  const [text, setText] = useState("");
  const [agents, setAgents] = useState([]);
  const [quickReplies, setQuickReplies] = useState([]);
  const [q, setQ] = useState("");
  const [searchMode, setSearchMode] = useState(false);
  const [transfer, setTransfer] = useState(false);
  const [online, setOnline] = useState([]);
  const [showReport, setShowReport] = useState(false);
  const inputRef = useRef();
  const navigate = useNavigate();
  const wantConv = params.get("conv");

  const loadList = useCallback(() => {
    if (searchMode) return;
    api.get("/support/inbox", { params: { filter: tab } }).then((r) => setConvs(r.data.conversations || [])).catch((e) => toast.error(apiError(e)));
  }, [tab, searchMode]);
  useEffect(() => { loadList(); const t = setInterval(loadList, 15000); return () => clearInterval(t); }, [loadList]);
  useEffect(() => { api.get("/support/agents").then((r) => setAgents(r.data.agents || [])).catch(() => {}); }, []);
  useEffect(() => { api.get("/support/quick-replies").then((r) => setQuickReplies(r.data.quick_replies || [])).catch(() => {}); }, []);
  useEffect(() => {
    const load = () => api.get("/support/online-people").then((r) => setOnline(r.data.people || [])).catch(() => setOnline([]));
    load(); const t = setInterval(load, 20000); return () => clearInterval(t);
  }, []);

  const openConv = useCallback((c) => {
    setActive(c);
    api.get(`/support/conversations/${c.id}/messages`).then((r) => setMsgs(r.data.messages || [])).catch(() => {});
    api.post(`/support/conversations/${c.id}/read`).then(loadList).catch(() => {});
  }, [loadList]);
  useEffect(() => { if (!active) return; const t = setInterval(() => api.get(`/support/conversations/${active.id}/messages`).then((r) => setMsgs(r.data.messages || [])).catch(() => {}), 8000); return () => clearInterval(t); }, [active]);
  // deep-link to a specific conversation (from Person Activity / notifications)
  useEffect(() => { if (wantConv) api.get(`/support/conversations/${wantConv}`).then((r) => openConv(r.data.conversation)).catch(() => {}); }, [wantConv]);

  const runSearch = async () => {
    if (!q.trim()) { setSearchMode(false); loadList(); return; }
    setSearchMode(true);
    try { const r = await api.get("/support/search", { params: { q } }); setConvs(r.data.conversations || []); setActive(null); }
    catch (e) { toast.error(apiError(e)); }
  };
  const clearSearch = () => { setQ(""); setSearchMode(false); loadList(); };

  const send = async () => {
    const body = text.trim(); if (!body || !active) return; setText("");
    try { await api.post(`/support/conversations/${active.id}/messages`, { message_body: body });
      const r = await api.get(`/support/conversations/${active.id}/messages`); setMsgs(r.data.messages || []); loadList(); }
    catch (e) { toast.error(apiError(e)); }
  };
  const assignMe = async () => {
    try { await api.post(`/support/conversations/${active.id}/assign`, { claim: true, user_id: myAgentId() }); toast.success("Assigned to you"); openConv(active); loadList(); }
    catch (e) { toast.error(apiError(e)); }
  };
  const doTransfer = async (uid, reason) => {
    try { await api.post(`/support/conversations/${active.id}/assign`, { user_id: uid, reason }); toast.success("Conversation transferred"); setTransfer(false); openConv(active); loadList(); }
    catch (e) { toast.error(apiError(e)); }
  };
  const close = async () => { try { await api.post(`/support/conversations/${active.id}/close`); toast.success("Conversation closed"); setActive(null); loadList(); } catch (e) { toast.error(apiError(e)); } };
  const myAgentId = () => { const me = agents.find((a) => a.online || true); return me?.user_id; }; // fallback; server enforces

  const eligibleAgents = agents.filter((a) => !active || a.organization_id === active.organization_id || a.organization_id == null);
  const startChat = async (cand, message) => {
    try { const { data } = await api.post("/support/conversations/start-with-candidate", { person_id: cand.person_id, message });
      toast.success("Support conversation started"); setSearchMode(false); setTab("mine");
      const r = await api.get(`/support/conversations/${data.conversation_id}`); openConv(r.data.conversation);
    } catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div data-testid="support-inbox">
      {/* toolbar */}
      <div className="flex flex-wrap items-center gap-2 mb-3">
        <div className="relative flex-1 min-w-[180px] max-w-sm">
          <Search className="w-4 h-4 text-slate-400 absolute left-2.5 top-2.5" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && runSearch()}
            placeholder="Search name, ID, email, message…" className="pl-8 h-9" data-testid="support-search-input" />
        </div>
        <Button size="sm" variant="outline" onClick={runSearch} data-testid="support-search-btn">Search</Button>
        {searchMode && <Button size="sm" variant="ghost" onClick={clearSearch} data-testid="support-search-clear">Clear</Button>}
        <Button size="sm" variant="outline" className="ml-auto" onClick={() => setShowReport((v) => !v)} data-testid="support-report-toggle"><BarChart3 className="w-4 h-4 mr-1" />Overview</Button>
      </div>

      {showReport && <ReportPanel />}
      {online.length > 0 && <OnlineCandidates online={online} onStart={startChat} />}

      <div className="flex flex-col lg:flex-row gap-4 h-[calc(100vh-320px)] min-h-[420px]">
        {/* List */}
        <div className={`lg:w-[340px] shrink-0 rounded-xl border border-slate-200 bg-white flex flex-col ${active ? "hidden lg:flex" : "flex"}`}>
          {!searchMode && (
            <div className="flex gap-1 p-2 border-b border-slate-100 overflow-x-auto tr-scroll">
              {TABS.map(([k, l]) => (
                <button key={k} onClick={() => { setTab(k); setActive(null); }} data-testid={`support-tab-${k}`}
                  className={`shrink-0 px-2.5 py-1.5 rounded-lg text-xs font-medium ${tab === k ? "bg-brand text-white" : "text-slate-500 hover:bg-slate-100"}`}>{l}</button>
              ))}
            </div>
          )}
          {searchMode && <div className="px-3 py-2 border-b border-slate-100 text-xs text-slate-500" data-testid="support-search-count">{convs.length} result{convs.length === 1 ? "" : "s"}</div>}
          <div className="flex-1 overflow-y-auto divide-y divide-slate-50">
            {convs.length === 0 && <div className="p-6"><EmptyState icon={MessageSquare} title={searchMode ? "No matches" : (tab === "closed" ? "No closed conversations yet." : "No conversations waiting.")} description={searchMode ? "Try a different search." : "You're all caught up."} /></div>}
            {convs.map((c) => (
              <button key={c.id} onClick={() => openConv(c)} data-testid={`support-conv-${c.id}`}
                className={`w-full text-left p-3 hover:bg-slate-50 flex gap-2.5 ${active?.id === c.id ? "bg-brand/5" : ""}`}>
                <UserAvatar name={c.person_name} size={34} />
                <div className="min-w-0 flex-1">
                  <div className="flex items-center justify-between gap-2"><p className="font-medium text-slate-800 text-sm truncate">{c.person_name}</p>
                    {c.unread > 0 && <span className="min-w-[16px] h-4 px-1 rounded-full bg-brand text-white text-[10px] font-bold flex items-center justify-center shrink-0">{c.unread}</span>}</div>
                  <p className="text-[11px] text-slate-400 truncate">{STATUS_LABEL[c.status]}{c.assigned_support_name ? ` · ${c.assigned_support_name}` : ""}</p>
                </div>
              </button>
            ))}
          </div>
        </div>

        {/* Conversation */}
        <div className={`flex-1 rounded-xl border border-slate-200 bg-white flex-col ${active ? "flex" : "hidden lg:flex"}`}>
          {!active ? <div className="flex-1 flex items-center justify-center"><EmptyState icon={MessageSquare} title="Select a conversation" description="Choose a support conversation to view and reply." /></div> : (
            <>
              <div className="flex items-center gap-2 p-3 border-b border-slate-100">
                <button onClick={() => setActive(null)} className="lg:hidden p-1.5 rounded-lg hover:bg-slate-100" data-testid="support-back"><ArrowLeft className="w-4 h-4" /></button>
                <UserAvatar name={active.person_name} size={32} />
                <div className="min-w-0 flex-1">
                  <p className="font-semibold text-slate-800 text-sm truncate" data-testid="support-conv-name">{active.person_name}</p>
                  <p className="text-[11px] text-slate-400 truncate">{active.candidate_type || "Candidate"} · {active.human_id} · {STATUS_LABEL[active.status]}</p>
                </div>
                {active.status !== "closed" && (
                  <>
                    <Button size="sm" variant="outline" onClick={assignMe} data-testid="support-assign-me"><UserPlus className="w-4 h-4 mr-1" />Me</Button>
                    <Button size="sm" variant="outline" onClick={() => setTransfer(true)} data-testid="support-transfer-open">Transfer</Button>
                    <Button size="sm" variant="outline" onClick={close} data-testid="support-close"><CheckCircle2 className="w-4 h-4 mr-1" />Close</Button>
                  </>
                )}
              </div>
              <div className="px-3 py-1.5 border-b border-slate-50 flex items-center gap-3 text-[11px] text-slate-400">
                <span>Assigned: {active.assigned_support_name || "Unassigned"}</span>
                {active.person_id && <button onClick={() => navigate(`/people/detail/${active.person_id}`)} className="text-brand flex items-center gap-1" data-testid="support-open-record">Open Candidate Record <ExternalLink className="w-3 h-3" /></button>}
              </div>
              <div className="flex-1 overflow-y-auto p-3 space-y-2 bg-slate-50" data-testid="support-thread">
                {msgs.map((m) => (
                  m.sender_type === "system" ? (
                    <p key={m.id} className="text-center text-[11px] text-slate-400 py-1" data-testid="support-system-event">{m.message_body}</p>
                  ) : (
                    <div key={m.id} className={`flex ${m.is_own ? "justify-end" : "justify-start"}`}>
                      <div className={`max-w-[70%] rounded-2xl px-3 py-2 text-sm ${m.is_own ? "bg-brand text-white rounded-br-sm" : "bg-white border border-slate-200 text-slate-700 rounded-bl-sm"}`}>
                        {!m.is_own && <p className="text-[10px] font-semibold text-slate-400 mb-0.5">{m.sender_name}</p>}
                        <p className="whitespace-pre-wrap break-words">{m.message_body}</p>
                      </div>
                    </div>
                  )
                ))}
              </div>
              {active.status !== "closed" ? (
                <div className="p-3 border-t border-slate-100 space-y-2">
                  {quickReplies.length > 0 && (
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <button className="inline-flex items-center gap-1.5 text-xs text-slate-500 rounded-lg border border-slate-200 px-2.5 py-1 hover:bg-slate-50" data-testid="quick-reply-trigger"><Zap className="w-3.5 h-3.5" />Quick Reply</button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="start" className="bg-white w-72 max-h-64 overflow-auto">
                        {quickReplies.map((qr) => (
                          <DropdownMenuItem key={qr.id} data-testid={`quick-reply-${qr.id}`} onClick={() => { setText((t) => (t ? t + " " : "") + qr.body); inputRef.current?.focus(); }}>
                            <span className="truncate"><span className="font-medium">{qr.title}</span></span>
                          </DropdownMenuItem>
                        ))}
                      </DropdownMenuContent>
                    </DropdownMenu>
                  )}
                  <div className="flex items-end gap-2">
                    <textarea ref={inputRef} value={text} onChange={(e) => setText(e.target.value)} rows={1} data-testid="support-reply-input"
                      onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
                      placeholder="Type a reply…" className="flex-1 resize-none rounded-lg border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand/30 max-h-24" />
                    <button onClick={send} data-testid="support-reply-send" className="rounded-lg bg-brand text-white p-2.5 hover:bg-brand-strong disabled:opacity-50" disabled={!text.trim()}><Send className="w-4 h-4" /></button>
                  </div>
                </div>
              ) : <div className="p-3 border-t border-slate-100 text-center text-xs text-slate-400">This conversation is closed. History is preserved.</div>}
            </>
          )}
        </div>
      </div>

      {transfer && <TransferDialog agents={eligibleAgents} onClose={() => setTransfer(false)} onTransfer={doTransfer} />}
    </div>
  );
}

function TransferDialog({ agents, onClose, onTransfer }) {
  const [uid, setUid] = useState("");
  const [reason, setReason] = useState("");
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-white max-w-md" data-testid="transfer-dialog">
        <DialogHeader><DialogTitle>Transfer conversation</DialogTitle>
          <DialogDescription>Reassign this support conversation to another eligible support user.</DialogDescription></DialogHeader>
        <div className="space-y-3">
          <div>
            <p className="text-xs font-semibold text-slate-600 mb-1">Assign to</p>
            <select value={uid} onChange={(e) => setUid(e.target.value)} data-testid="transfer-target" className="w-full h-10 rounded-md border border-slate-200 px-2 text-sm">
              <option value="">Select support user…</option>
              {agents.map((a) => <option key={a.user_id} value={a.user_id}>{a.user_name}</option>)}
            </select>
          </div>
          <div>
            <p className="text-xs font-semibold text-slate-600 mb-1">Reason for transfer <span className="font-normal text-slate-400">(internal only — the candidate never sees this)</span></p>
            <Input value={reason} onChange={(e) => setReason(e.target.value)} data-testid="transfer-reason" placeholder="e.g. Licensing question — routing to D88 specialist" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button className="bg-brand hover:bg-brand-strong" disabled={!uid} onClick={() => onTransfer(uid, reason)} data-testid="transfer-confirm">Transfer</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function OnlineCandidates({ online, onStart }) {
  const [invite, setInvite] = useState(null);
  return (
    <div className="mb-4 rounded-xl border border-emerald-200 bg-emerald-50/50 p-3" data-testid="online-candidates">
      <p className="text-sm font-semibold text-slate-700 flex items-center gap-2 mb-2"><Users2 className="w-4 h-4 text-emerald-600" />Online People <span className="text-emerald-700">· {online.length}</span></p>
      <div className="flex gap-2 overflow-x-auto tr-scroll pb-1">
        {online.map((c) => (
          <div key={c.person_id} className="shrink-0 w-56 rounded-lg bg-white border border-slate-200 p-3" data-testid={`online-cand-${c.person_id}`}>
            <p className="text-sm font-medium text-slate-800 flex items-center gap-1.5"><Circle className="w-2 h-2 fill-emerald-500 text-emerald-500" />{c.person_name}</p>
            <p className="text-[11px] text-slate-400 mb-2 truncate">{(c.classifications || []).map((x) => x.replace(/_/g, " ")).join(", ") || "Candidate"}{c.organization_name ? ` · ${c.organization_name}` : ""}</p>
            {c.chat_eligible !== false
              ? <Button size="sm" className="w-full bg-brand hover:bg-brand-strong h-8 text-xs" onClick={() => setInvite(c)} data-testid={`start-chat-${c.person_id}`}>Start Chat</Button>
              : <p className="text-[11px] text-slate-400 text-center">Chat not available</p>}
          </div>
        ))}
      </div>
      {invite && <StartChatDialog cand={invite} onClose={() => setInvite(null)} onStart={onStart} />}
    </div>
  );
}

function StartChatDialog({ cand, onClose, onStart }) {
  const [msg, setMsg] = useState("");
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-white max-w-md" data-testid="start-chat-dialog">
        <DialogHeader><DialogTitle>Start support chat with {cand.person_name}</DialogTitle>
          <DialogDescription>Send this candidate a friendly support invitation.</DialogDescription></DialogHeader>
        <p className="text-xs text-slate-500">The candidate will see a friendly invitation. You can optionally add a short opening message.</p>
        <Input value={msg} onChange={(e) => setMsg(e.target.value)} placeholder="Optional opening message…" data-testid="start-chat-message" />
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button className="bg-brand hover:bg-brand-strong" onClick={() => { onStart(cand, msg.trim()); onClose(); }} data-testid="start-chat-confirm">Start Chat</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ReportPanel() {
  const [rep, setRep] = useState(null);
  const [cov, setCov] = useState(null);
  useEffect(() => {
    api.get("/support/report").then((r) => setRep(r.data)).catch(() => setRep(null));
    api.get("/support/coverage").then((r) => setCov(r.data)).catch(() => setCov(null));
  }, []);
  const Stat = ({ label, value, testId }) => (
    <div className="rounded-lg border border-slate-200 bg-white px-3 py-2 text-center" data-testid={testId}>
      <p className="text-lg font-bold text-slate-800">{value}</p><p className="text-[11px] text-slate-400">{label}</p>
    </div>
  );
  return (
    <div className="mb-4 space-y-2" data-testid="support-report">
      {rep && (
        <div className="grid grid-cols-3 sm:grid-cols-5 gap-2">
          <Stat label="New (period)" value={rep.new_conversations} testId="report-new" />
          <Stat label="Open" value={rep.open_conversations} testId="report-open" />
          <Stat label="Waiting" value={rep.waiting_for_staff} testId="report-waiting" />
          <Stat label="Closed" value={rep.closed_conversations} testId="report-closed" />
          <Stat label="Unassigned" value={rep.unassigned} testId="report-unassigned" />
        </div>
      )}
      {cov && (
        <div className="rounded-lg border border-slate-200 bg-white px-3 py-2 flex flex-wrap gap-4 text-xs text-slate-600" data-testid="support-coverage">
          <span className="font-semibold text-slate-500">Support Coverage:</span>
          <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-emerald-500" />{cov.available} Available</span>
          <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-amber-500" />{cov.away} Away</span>
          <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-slate-400" />{cov.offline} Offline</span>
          <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-brand" />{cov.waiting_for_staff} Waiting for Staff</span>
        </div>
      )}
    </div>
  );
}
